import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { siteSections } from "@/data/siteMap";

const overviewContent = {
  about: {
    intro: "TraceElements is a boutique operating firm built by engineers and operators. Explore how we structure the organization, invest in our people, and hold ourselves accountable.",
  },
  solutions: {
    intro: "From AI and cloud to regulatory compliance and transaction services — every TraceElements solution is engineered, project-managed, and audited under one accountable roof.",
  },
  industries: {
    intro: "We staff every engagement with practitioners who have lived the market they're advising. Regulatory nuance, market pressure, and operating rhythm — encoded.",
  },
  careers: {
    intro: "We hire engineers and operators who need momentum. Compensation is transparent, ownership is real, and the work ships.",
  },
};

export default function SectionOverview({ section }) {
  const meta = siteSections[section];
  if (!meta) return <Navigate to="/" replace />;
  const oc = overviewContent[section] || {};

  return (
    <div className="bg-[#0B0C10]" data-testid={`overview-${section}`}>
      {/* Hero */}
      <section className="relative border-b border-[#1F2833] py-20 lg:py-28 overflow-hidden">
        <div className="absolute inset-0 te-grid-bg-dense opacity-30" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#B91C2C] mb-6">{meta.landing.kicker}</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tighter leading-[0.95] max-w-5xl">
            {meta.landing.title}
          </h1>
          <p className="mt-6 font-display text-xl text-[#FF3355] max-w-3xl">{meta.landing.tagline}</p>
          {oc.intro && <p className="mt-6 text-[#C5C6C7] max-w-3xl leading-relaxed">{oc.intro}</p>}
        </div>
      </section>

      {/* Grid */}
      <section className="py-16 lg:py-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#B91C2C] mb-6">// {meta.label.toUpperCase()} · {meta.items.length} tracks</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#1F2833] border border-[#1F2833]">
            {meta.items.map((it, idx) => (
              <Link
                key={it.slug}
                to={it.href || `/${section}/${it.slug}`}
                data-testid={`overview-card-${it.slug}`}
                className="bg-[#0B0C10] p-6 te-glow-hover border border-transparent hover:border-[#B91C2C] group"
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#B91C2C]">
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                  <ArrowUpRight size={16} className="text-[#FF3355] opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                <h3 className="mt-4 font-display text-xl font-bold tracking-tight text-[#F5F5F7] group-hover:text-[#FF3355] transition-colors">
                  {it.label}
                </h3>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 border-t border-[#1F2833]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#B91C2C] mb-3">// NEXT.STEP</div>
            <h3 className="font-display text-2xl lg:text-4xl font-bold tracking-tight max-w-2xl">Scope your engagement with the founders.</h3>
          </div>
          <Link to="/terminal" data-testid="overview-cta" className="inline-flex items-center gap-2 bg-[#B91C2C] text-[#0B0C10] px-7 py-4 font-mono text-xs uppercase tracking-[0.22em] font-bold hover:bg-[#FF3355] w-fit">
            Initialize Project <ArrowUpRight size={14} />
          </Link>
        </div>
      </section>
    </div>
  );
}
